import { useState } from "react";
import axios from "axios";
import { toast } from "sonner";
import { Loader2 } from "lucide-react";
import BgOrnaments from "@/components/vu/BgOrnaments";
const API = `${process.env.REACT_APP_BACKEND_URL}/api`;
const EMAIL_RE = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
const PHONE_RE = /^[+]?[\d\s()-]{7,20}$/;

export default function Contact() {
    const [f, setF] = useState({ first: "", last: "", email: "", phone: "", service: "", message: "" });
    const [loading, setLoading] = useState(false);
    const set = (k) => (e) => setF({ ...f, [k]: e.target.value });
    const setPhone = (e) => {
        const digits = e.target.value.replace(/\D/g, "").slice(0, 10);
        setF({ ...f, phone: digits });
    };
    const submit = async (e) => {
        e.preventDefault();
        if (!f.first || !f.email || !f.message) { toast.error("Please fill in your first name, email and a short message."); return; }
        if (!EMAIL_RE.test(f.email)) { toast.error("Please enter a valid email address."); return; }
        if (f.phone && f.phone.length !== 10) { toast.error("Please enter a valid 10-digit phone number."); return; }
        setLoading(true);
        try {
            await axios.post(`${API}/inquiries`, { name: `${f.first} ${f.last}`.trim(), email: f.email, phone: f.phone ? `+1${f.phone}` : "", service: f.service, message: f.message });
            if (window.gtag) {
                window.gtag('event', 'generate_lead', {
                    currency: 'USD',
                    service_interest: f.service || 'unspecified',
                });
            }

            if (window.fbq) {
                window.fbq('track', 'Lead');
            }

            toast.success("Got it — we'll be in touch within one business day.");
            setF({ first: "", last: "", email: "", phone: "", service: "", message: "" });
        } catch (err) { console.error(err); toast.error("Something went wrong. Please try again."); }
        finally { setLoading(false); }
    };
    return (
        <section id="contact" data-testid="contact-section" className="relative py-[110px] md:py-[130px] border-t border-[#1F3A2B] overflow-hidden">
            <BgOrnaments variant="default" />
            <div className="max-w-[1240px] mx-auto px-8">
                <div className="text-center max-w-[640px] mx-auto mb-14">
                    <div className="inline-flex items-center gap-2 px-3.5 py-1.5 border border-[#2A4A38] bg-[#152A20]/60 backdrop-blur rounded-full text-[11.5px] text-[#B8CFC0] mb-5">
                        <span className="w-[7px] h-[7px] rounded-full bg-[#DFFF3D]" /> Contact Us
                    </div>
                    <h2 className="font-display text-[#F5F0E8] tracking-tight leading-[1.02]" style={{ fontSize: "clamp(2.4rem, 4.4vw, 3.4rem)" }}>
                        Let's map out your<br />
                        <span className="italic-serif text-[#DFFF3D]">next 90 days.</span>
                    </h2>
                    <p className="mt-5 text-[15.5px] text-[#7A9088]">Tell us a little about the brand and where you want to be next quarter. We'll come back within one business day with a rough plan.</p>
                </div>

                <div className="max-w-[1080px] mx-auto grid lg:grid-cols-[.9fr_1.1fr] gap-5">
                    {/* Left: contact channels */}
                    <div className="relative bg-[#152A20] border border-[#1F3A2B] rounded-[24px] p-8 md:p-10 flex flex-col justify-between overflow-hidden">
                        <div className="absolute -top-20 -left-20 w-64 h-64 rounded-full pointer-events-none" style={{ background: "radial-gradient(circle, rgba(255,122,46,0.14), transparent 60%)" }} />
                        <div className="relative">
                            <h3 className="font-display text-[#F5F0E8] text-[22px] mb-6">Prefer to talk?</h3>
                            <div className="space-y-6">
                                {[
                                    { l: "EMAIL", ic: "fa-envelope", v: "info@vusolutions-dev.com", href: "mailto:info@vusolutions-dev.com" },
                                    { l: "PHONE / WHATSAPP", ic: "fa-phone", v: "+1 (610) 445-0243", href: "tel:+16104450243" },
                                    { l: "STUDIO HOURS", ic: "fa-clock", v: "Mon–Fri · 9am–6pm ET", href: null },
                                ].map((r) => (
                                    <div key={r.l} className="flex items-start gap-4">
                                        <div className="w-11 h-11 rounded-xl bg-gradient-to-br from-[#DFFF3D]/20 to-[#DFFF3D]/5 border border-[#DFFF3D]/25 flex items-center justify-center shrink-0">
                                            <i className={`fa-solid ${r.ic} text-[#DFFF3D]`} />
                                        </div>
                                        <div className="min-w-0">
                                            <div className="mono text-[10px] text-[#7A9088] mb-1">{r.l}</div>
                                            {r.href ? <a href={r.href} className="text-[#F5F0E8] font-medium hover:text-[#DFFF3D] transition-colors break-all">{r.v}</a> : <div className="text-[#F5F0E8] font-medium">{r.v}</div>}
                                        </div>
                                    </div>
                                ))}
                            </div>
                        </div>
                        <div className="relative mt-10 pt-8 border-t border-[#1F3A2B]">
                            <div className="mono text-[10px] text-[#7A9088] mb-3">FOLLOW US</div>
                            <div className="flex gap-2.5">
                                {["instagram", "facebook-f", "linkedin-in"].map((s) => (
                                    <a key={s} href={s === "facebook-f" ? "https://www.facebook.com/vusolutionsdev/" : "#"} target="_blank" rel="noopener noreferrer" className="w-10 h-10 rounded-full border border-[#1F3A2B] flex items-center justify-center text-[#F5F0E8] hover:bg-[#DFFF3D] hover:text-[#0E1F16] hover:border-[#DFFF3D] transition-colors">
                                        <i className={`fa-brands fa-${s} text-sm`} />
                                    </a>
                                ))}                          
                            </div>
                        </div>
                    </div>

                    {/* Right: form */}
                    <form onSubmit={submit} data-testid="contact-form" className="bg-[#152A20] border border-[#1F3A2B] rounded-[24px] p-8 md:p-10">
                        <div className="flex items-center gap-3 mb-8">
                            <div className="w-10 h-10 rounded-xl bg-gradient-to-br from-[#DFFF3D]/20 to-[#DFFF3D]/5 border border-[#DFFF3D]/25 flex items-center justify-center">
                                <i className="fa-solid fa-paper-plane text-[#DFFF3D] text-[15px]" />
                            </div>
                            <h3 className="font-display text-[#F5F0E8] text-[22px]">Request a free consultation</h3>
                        </div>
                        <div className="grid grid-cols-1 sm:grid-cols-2 gap-6 mb-6">
                            <Field label="First name" placeholder="John" val={f.first} on={set("first")} tid="contact-input-first" />
                            <Field label="Last name" placeholder="Doe" val={f.last} on={set("last")} tid="contact-input-last" />
                        </div>
                        <div className="grid grid-cols-1 sm:grid-cols-2 gap-6 mb-6">
                            <Field label="Email" type="email" placeholder="you@brand.com" val={f.email} on={set("email")} tid="contact-input-email" />
                            <div>
                                <label className="mono text-[11px] text-[#7A9088] mb-2.5 block tracking-wider">PHONE</label>
                                <div className="flex items-center w-full border border-[#1F3A2B] bg-[#0E1F16] rounded-xl px-5 min-h-[56px] focus-within:outline-2 focus-within:outline-[#DFFF3D]">
                                    <span className="text-[#7A9088] text-[15px] mr-2 select-none">+1</span>
                                    <input
                                        data-testid="contact-input-phone"
                                        type="tel"
                                        inputMode="numeric"
                                        value={f.phone}
                                        onChange={setPhone}
                                        placeholder="610 445 0243"
                                        maxLength={10}
                                        className="flex-1 bg-transparent text-[#F5F0E8] text-[15px] py-4 outline-none placeholder:text-[#7A9088]"
                                    />
                                </div>
                            </div>
                        </div>
                        <div className="mb-6">
                            <label className="mono text-[11px] text-[#7A9088] mb-2.5 block tracking-wider">SERVICE OF INTEREST</label>
                            <select data-testid="contact-input-service" value={f.service} onChange={set("service")} className="w-full border border-[#1F3A2B] bg-[#0E1F16] text-[#F5F0E8] rounded-xl px-5 py-4 text-[15px] min-h-[56px] focus:outline-2 focus:outline-[#DFFF3D] appearance-none cursor-pointer">
                                <option value="">Choose one…</option>
                                    {["Website Design & Development", "Google Business Profile & Local SEO", "Google & Meta Ads", "Email & SMS Campaigns", "AI Voice Agent / Phone System", "Everything (full retainer)"].map((s) => <option key={s} value={s}>{s}</option>)}
                            </select>
                        </div>
                        <div className="mb-8">
                            <label className="mono text-[11px] text-[#7A9088] mb-2.5 block tracking-wider">MESSAGE</label>
                            <textarea data-testid="contact-input-message" value={f.message} onChange={set("message")} rows={5} placeholder="Tell us about your business and where you want to be in 90 days…" className="w-full border border-[#1F3A2B] bg-[#0E1F16] text-[#F5F0E8] rounded-xl px-5 py-4 text-[15px] min-h-[160px] leading-relaxed resize-y focus:outline-2 focus:outline-[#DFFF3D] placeholder:text-[#7A9088]" />
                        </div>
                        <div className="flex items-center justify-between gap-4 flex-wrap">
                            <p className="text-[12px] text-[#7A9088] max-w-[260px]">By submitting, you agree to our lightweight privacy note. No spam, ever.</p>
                            <button type="submit" disabled={loading} data-testid="contact-submit" className="vu-btn vu-cta-pulse inline-flex items-center gap-2 bg-[#DFFF3D] text-[#0E1F16] px-7 py-3.5 rounded-full font-semibold text-[14px] hover:bg-[#C7E82F] disabled:opacity-60 disabled:cursor-not-allowed">
                                {loading ? <><Loader2 className="w-4 h-4 animate-spin" />Sending…</> : <>Send inquiry <i className="fa-solid fa-arrow-right text-xs" /></>}
                            </button>
                        </div>
                    </form>
                </div>
            </div>
        </section>
    );
}
function Field({ label, val, on, placeholder, type = "text", tid }) {
    return (
        <div>
            <label className="mono text-[11px] text-[#7A9088] mb-2.5 block tracking-wider">{label.toUpperCase()}</label>
            <input data-testid={tid} type={type} value={val} onChange={on} placeholder={placeholder} className="w-full border border-[#1F3A2B] bg-[#0E1F16] text-[#F5F0E8] rounded-xl px-5 py-4 text-[15px] min-h-[56px] focus:outline-2 focus:outline-[#DFFF3D] placeholder:text-[#7A9088]" />
        </div>
    );
}
